import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { Observable } from "@apollo/client/utilities";

import { clearStoredTokens, getValidAccessToken, refreshAccessToken } from "../utils/authRefresh";

const uri_string = `https://${window.location.host}/api/graphql`;

const httpLink = new HttpLink({ uri: uri_string });

const authLink = setContext(async (_, { headers }) => {
  const accessToken = await getValidAccessToken();
  return {
    headers: {
      ...headers,
      authorization: accessToken ? `Bearer ${accessToken}` : "",
    },
  };
});

let isRefreshing = false;
let pendingRequests: Array<(newAccessToken: string | null) => void> = [];

const addPendingRequest = (cb: (newAccessToken: string | null) => void) => {
  pendingRequests.push(cb);
};

const resolvePendingRequests = async (accessToken?: string | null) => {
  const newAccess = accessToken ?? (await getValidAccessToken({ minTtlSeconds: 0 }));
  pendingRequests.forEach((cb) => cb(newAccess ?? null));
  pendingRequests = [];
};

const rejectPendingRequests = (err?: unknown) => {
  pendingRequests.forEach((cb) => cb(null));
  pendingRequests = [];
};

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  interface MaybeServerError {
    statusCode?: number;
    result?: { errors?: Array<{ message?: string }> };
  }
  const ne = networkError as MaybeServerError | null | undefined;
  const statusCode = ne?.statusCode;
  const isUnauthorizedMsg = Boolean(graphQLErrors?.some((e) => e.message === "Unauthorized"));
  const isUnauthenticatedCode = Boolean(graphQLErrors?.some((e) => (e as any)?.extensions?.code === "UNAUTHENTICATED"));
  const isUserNotFoundMsg = Boolean(
    graphQLErrors?.some((e) => e.message === "Context creation failed: User not found")
  );

  const unauthorized = Boolean(
    statusCode === 401 || isUnauthorizedMsg || isUnauthenticatedCode || (statusCode === 400 && isUserNotFoundMsg)
  );

  if (!unauthorized) {
    return;
  }

  if (operation.operationName === "RefreshToken") {
    clearStoredTokens("refresh_operation_failed");
    return;
  }

  if (isRefreshing) {
    // queue the subsequent failed operations
    return new Observable((observer) => {
      addPendingRequest(async (token: string | null) => {
        if (!token) {
          observer.error(networkError ?? new Error("Unauthorized"));
          return;
        }
        operation.setContext(({ headers = {} }) => ({
          headers: { ...headers, authorization: token ? `Bearer ${token}` : "" },
        }));
        forward(operation).subscribe({
          next: observer.next.bind(observer),
          error: observer.error.bind(observer),
          complete: observer.complete.bind(observer),
        });
      });
    });
  }

  isRefreshing = true;
  return new Observable((observer) => {
    // queue the first failed operation
    addPendingRequest(async (token: string | null) => {
      if (!token) {
        observer.error(networkError ?? new Error("Unauthorized"));
        return;
      }
      operation.setContext(({ headers = {} }) => ({
        headers: { ...headers, authorization: token ? `Bearer ${token}` : "" },
      }));
      forward(operation).subscribe({
        next: observer.next.bind(observer),
        error: observer.error.bind(observer),
        complete: observer.complete.bind(observer),
      });
    });

    refreshAccessToken()
      .then(async (result) => {
        isRefreshing = false;
        if (!result?.accessToken) {
          rejectPendingRequests(networkError ?? new Error("Unauthorized"));
          return;
        }
        await resolvePendingRequests(result.accessToken);
      })
      .catch((err) => {
        isRefreshing = false;
        rejectPendingRequests(err);
      });
  });
});

// afterware removed; tokens are managed via refresh flow

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([authLink, errorLink, httpLink]),
  cache: new InMemoryCache(),
});
